import React, { useEffect, useState, useCallback } from 'react';
import MapEngine from '../components/harvesting/MapEngine';
import { getAggregateHarvesting } from '../services/aggregateService';

// ─── Shared styles ─────────────────────────────────────────────────────────────
const S = {
  page:    { padding: '28px 32px', maxWidth: 1200, margin: '0 auto' },
  heading: { fontSize: 24, fontWeight: 700, marginBottom: 6 },
  sub:     { color: '#6b7280', fontSize: 14, marginBottom: 20 },
  row:     { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 },
  stat:    {
    background: '#f9fafb', border: '1px solid #e5e7eb',
    borderRadius: 8, padding: '12px 18px', textAlign: 'center', minWidth: 110,
  },
  statNum:  { fontSize: 22, fontWeight: 700, color: '#92400e' },
  statLbl:  { fontSize: 12, color: '#6b7280', marginTop: 2 },
  filters:  {
    background: '#fff', border: '1px solid #e5e7eb',
    borderRadius: 8, padding: '14px 18px', marginBottom: 20,
    display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end',
  },
  label:   { fontSize: 12, color: '#374151', fontWeight: 600, marginBottom: 4 },
  select:  {
    padding: '7px 10px', border: '1px solid #d1d5db',
    borderRadius: 6, fontSize: 13, background: '#fff',
  },
  input:   {
    padding: '7px 10px', border: '1px solid #d1d5db',
    borderRadius: 6, fontSize: 13, minWidth: 140,
  },
  btn:     (bg = '#1d4ed8') => ({
    padding: '7px 16px', background: bg, color: '#fff',
    border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
  }),
  err:     {
    background: '#fef2f2', border: '1px solid #fca5a5',
    borderRadius: 6, padding: '10px 14px', color: '#991b1b', fontSize: 14, marginBottom: 14,
  },
  detail:  {
    marginTop: 16, background: '#fff', border: '1px solid #e5e7eb',
    borderRadius: 8, padding: '14px 18px',
  },
};

const CATEGORIES = [
  { value: '', label: 'All Categories' },
  { value: 'plants_medicine', label: '🌿 Plants & Medicine' },
  { value: 'hunting',         label: '🦌 Hunting' },
  { value: 'birds',           label: '🦅 Birds' },
  { value: 'fishing',         label: '🎣 Fishing' },
  { value: 'environmental',   label: '⚠️ Environmental' },
  { value: 'other',           label: '📍 Other' },
];

const toDataLayers = (records) => {
  const groups = {};
  records.forEach((r) => {
    const cat = r.category || 'other';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(r);
  });
  return Object.entries(groups).map(([dataTypeId, data]) => ({ dataTypeId, data }));
};

const AggregateHarvestingMapPage = () => {
  const [records,   setRecords]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [selected,  setSelected]  = useState(null);
  const [category,  setCategory]  = useState('');
  const [species,   setSpecies]   = useState('');
  const [fromDate,  setFromDate]  = useState('');
  const [toDate,    setToDate]    = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (category) params.category = category;
      if (species)  params.species  = species;
      if (fromDate) params.fromDate = fromDate;
      if (toDate)   params.toDate   = toDate;
      const { data } = await getAggregateHarvesting(params);
      setRecords(data.records || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load harvest map data.');
    } finally {
      setLoading(false);
    }
  }, [category, species, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  const communities = new Set(records.map(r => r.tenantId?.name).filter(Boolean)).size;
  const dataLayers  = toDataLayers(records);

  return (
    <div style={S.page}>
      <h1 style={S.heading}>Harvest Activity Map</h1>
      <p style={S.sub}>
        Cross-community view of approved harvest activity. Filter by category, species, or date
        range.
      </p>

      {/* ── Filters ── */}
      <div style={S.filters}>
        <div>
          <div style={S.label}>Category</div>
          <select style={S.select} value={category} onChange={e => setCategory(e.target.value)}>
            {CATEGORIES.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>
        <div>
          <div style={S.label}>Species</div>
          <input
            style={S.input}
            placeholder="e.g. Moose"
            value={species}
            onChange={e => setSpecies(e.target.value)}
          />
        </div>
        <div>
          <div style={S.label}>From Date</div>
          <input
            type="date"
            style={S.input}
            value={fromDate}
            onChange={e => setFromDate(e.target.value)}
          />
        </div>
        <div>
          <div style={S.label}>To Date</div>
          <input
            type="date"
            style={S.input}
            value={toDate}
            onChange={e => setToDate(e.target.value)}
          />
        </div>
        <button style={S.btn()} onClick={load}>Apply Filters</button>
        <button
          style={S.btn('#6b7280')}
          onClick={() => { setCategory(''); setSpecies(''); setFromDate(''); setToDate(''); }}
        >
          Clear
        </button>
      </div>

      {/* ── Stats ── */}
      <div style={S.row}>
        <div style={S.stat}>
          <div style={S.statNum}>{records.length}</div>
          <div style={S.statLbl}>Records</div>
        </div>
        <div style={S.stat}>
          <div style={S.statNum}>{communities}</div>
          <div style={S.statLbl}>Communities</div>
        </div>
        <div style={S.stat}>
          <div style={S.statNum}>{dataLayers.length}</div>
          <div style={S.statLbl}>Categories</div>
        </div>
      </div>

      {error && <div style={S.err}>{error}</div>}

      {loading ? (
        <p style={{ color: '#6b7280' }}>Loading map data…</p>
      ) : (
        <MapEngine
          showHarvestTerritory
          dataLayers={dataLayers}
          height="560px"
          onFeatureClick={setSelected}
        />
      )}

      {selected && (
        <div style={S.detail}>
          <strong>Selected Record</strong>
          <div style={{ fontSize: 13, marginTop: 8, color: '#374151' }}>
            <div><b>Species:</b>    {selected.species    || '—'}</div>
            <div><b>Category:</b>   {selected.category   || '—'}</div>
            <div><b>Community:</b>  {selected.tenantId?.name || '—'}</div>
            <div><b>Season:</b>     {selected.season     || '—'}</div>
            <div><b>Quantity:</b>   {selected.quantity   ?? '—'}</div>
            <div><b>Traditional:</b> {selected.isTraditionalUse ? 'Yes' : 'No'}</div>
            <div><b>Date:</b>       {selected.harvestDate ? new Date(selected.harvestDate).toLocaleDateString() : '—'}</div>
          </div>
          <button
            onClick={() => setSelected(null)}
            style={{ marginTop: 10, fontSize: 12, cursor: 'pointer', background: 'none', border: 'none', color: '#6b7280' }}
          >
            ✕ Close
          </button>
        </div>
      )}
    </div>
  );
};

export default AggregateHarvestingMapPage;
